"use client";

import type { RealtimeChannel, SupabaseClient } from "@supabase/supabase-js";

/**
 * Satu pintu untuk semua langganan live aplikasi ini, di atas **Broadcast**
 * (mig 4440) — bukan lagi `postgres_changes`.
 *
 * Alasan pindah ada di kepala migrasinya; ringkasnya: poller
 * `postgres_changes` menghabiskan 65% waktu eksekusi database dan 86% lalu
 * lintas buffer PROD untuk 2 MB perubahan nyata per 16 hari. Broadcast
 * membalik biayanya jadi O(peristiwa nyata).
 *
 * ── DUA HAL YANG BERBEDA DARI `postgres_changes`, DAN KENAPA PENTING ────────
 *
 * 1. **Channel-nya PRIVATE.** Otorisasinya policy `select` di
 *    `realtime.messages`, dievaluasi sekali saat subscribe dengan JWT
 *    pemanggil. Tanpa JWT terpasang di soket Realtime, subscribe ditolak —
 *    karena itu `setAuth()` dipanggil eksplisit di bawah, tidak menumpang
 *    pemasangan otomatis supabase-js yang bergantung pada urutan event auth.
 *
 * 2. **Gagalnya SENYAP.** Topic yang salah ketik, atau policy yang menolak,
 *    tidak melempar apa-apa — channel-nya hanya tak pernah menerima pesan.
 *    Itu mode kegagalan yang jauh lebih jahat daripada error, dan alasan
 *    `onDegraded` ada: begitu channel tidak mencapai SUBSCRIBED, pemanggil
 *    disuruh menyegarkan berkala sendiri. Datanya jadi agak basi, bukan mati.
 *
 * ── SATU CHANNEL PER (KLIEN, TOPIC) — REGISTRY BER-REFCOUNT (audit 2026-09-02)
 *
 * realtime-js memulangkan channel yang SAMA untuk topic yang sama
 * (`RealtimeClient.channel()`), dan `.subscribe()` kedua pada channel yang
 * sudah bergabung adalah no-op: callback statusnya tak pernah terdaftar.
 * `removeChannel()` oleh satu pemilik menutup channel milik pemilik lain.
 * Di PROD itu nyata: `admin-notif:<uid>` dipegang `AdminNotificationBell`
 * (dirender ulang di 59 halaman admin) DAN `AdminPendingCountsProvider`
 * (AdminShell) — tiap navigasi lonceng melepas channel-nya, provider ikut
 * kehilangan langganan tanpa pernah tahu.
 *
 * Karena itu langganan disatukan di sini: satu channel per (klien, topic),
 * handler tiap pemanggil di-fan-out dari satu binding, dan channel baru
 * benar-benar dilepas saat pemanggil TERAKHIR pergi.
 *
 * ── DEGRADED BUKAN AKHIR — SAMBUNG ULANG BER-BACKOFF (audit 2026-09-02) ─────
 *
 * Versi lama membuang channel SELAMANYA setelah tiga galat beruntun, dan
 * tab yang hidup berjam-jam (PWA, tab latar) terus polling 60 detik sampai
 * di-reload: ±12 rb request/hari di PROD (8% edge_logs), pita 50–70 detik
 * yang tajam di histogram jarak antar-request per sesi. Sekarang:
 *
 *   • `CHANNEL_ERROR` / `TIMED_OUT` — realtime-js mencoba rejoin sendiri;
 *     setelah `MAX_CONSECUTIVE_CHANNEL_ERRORS` channel dilepas lalu dibuat
 *     ULANG dengan backoff 1 → 2 → 5 → 10 menit (JWT diambil segar dari
 *     `getSession()` tiap kali). Backoff kembali nol saat SUBSCRIBED.
 *   • `CLOSED` — phoenix MENGHAPUS channel dari soket dan tidak pernah
 *     rejoin, jadi ini justru yang paling butuh dibuat ulang: langsung
 *     dilepas dan dijadwalkan ulang, tanpa menunggu tiga galat.
 *   • `Unauthorized` (policy menolak) — tetap PERMANEN. Menyambung ulang
 *     dengan JWT yang sama hanya menghasilkan derau; kepala berkas lama
 *     mencatat 6 percikan "Unauthorized" dalam 50 detik dari satu tab.
 *   • Tab kembali terlihat (`visibilitychange`) — channel yang sedang
 *     menunggu backoff langsung dicoba lagi; tick degraded dilewati saat
 *     `document.hidden`.
 *
 * Mode degraded tetap ada sebagai jalan mundur, dengan irama bawaan 3 menit
 * (dulu 1 menit) — pemanggil yang datanya wajib lebih segar menetapkan
 * `degradedIntervalMs` sendiri, yang halaman uang memang sudah lakukan lewat
 * `refetchOnMount: "always"`.
 */

/** Muatan broadcast dari trigger mig 4440. Sengaja seminimal itu — broadcast
 *  tak punya RLS per-baris, jadi tiap kolom tambahan adalah permukaan baru. */
export type LivePayload = {
  id?: string;
  table?: string;
  is_read?: boolean;
  type?: string;
  important?: boolean | null;
};

/** `tg_op` dari trigger: nama operasinya, huruf besar. */
export type LiveEvent = "INSERT" | "UPDATE" | "DELETE";

type Options = {
  /**
   * Dipanggil saat langganan live TIDAK bisa diandalkan (channel error, timeout,
   * ditutup server, atau tak ada sesi sama sekali). Pemanggil memakainya untuk
   * menyegarkan datanya sendiri; helper ini yang mengatur iramanya.
   */
  onDegraded?: () => void;
  /** Irama penyegaran cadangan saat degraded. Default 3 menit. */
  degradedIntervalMs?: number;
};

export const DEFAULT_DEGRADED_INTERVAL_MS = 180_000;

/**
 * Berapa kali `CHANNEL_ERROR`/`TIMED_OUT` beruntun ditoleransi sebelum channel
 * dilepas dan dibuat ulang ber-backoff. realtime-js menyambung ulang channel
 * yang gagal SENDIRI di antara galat-galat itu; ambang ini menangkap kasus yang
 * tak kunjung sembuh dari rejoin bawaan (soket yang membusuk, JWT basi).
 */
export const MAX_CONSECUTIVE_CHANNEL_ERRORS = 3;

/** Jeda sebelum channel dibuat ulang, per percobaan; percobaan selanjutnya
 *  memakai nilai terakhir. */
export const RESUBSCRIBE_BACKOFF_MS: readonly number[] = [
  60_000, 120_000, 300_000, 600_000,
];

/**
 * Apakah galat subscribe berarti "kamu memang tidak berhak", bukan "coba lagi"?
 *
 * Realtime menolak channel privat lewat pesan berbentuk
 * `Unauthorized: You do not have permissions to read from this Channel topic: <topic>`.
 * Penolakan seperti itu tak akan berubah tanpa JWT baru, jadi menyambung ulang
 * hanya menghasilkan derau. Pencocokannya sengaja longgar (dua kata kunci, tak
 * peduli huruf besar-kecil) supaya perubahan kata di sisi server tidak diam-diam
 * menghidupkan lagi loop-nya; ambang `MAX_CONSECUTIVE_CHANNEL_ERRORS` tetap
 * menjadi jaring pengaman kalau pesannya berubah total.
 */
export function isUnauthorizedChannelError(err: unknown): boolean {
  const text =
    typeof err === "string"
      ? err
      : err instanceof Error
        ? err.message
        : typeof (err as { message?: unknown } | null)?.message === "string"
          ? ((err as { message: string }).message)
          : "";
  if (!text) return false;
  const lower = text.toLowerCase();
  return lower.includes("unauthorized") || lower.includes("permission");
}

type Handler = (event: LiveEvent, payload: LivePayload) => void;
type LiveStatus = "SUBSCRIBED" | "DEGRADED";
type StatusListener = (status: LiveStatus) => void;

/** Satu channel bersama per (klien, topic) beserta semua pemanggilnya. */
type Entry = {
  db: SupabaseClient;
  topic: string;
  channel: RealtimeChannel | null;
  handlers: Set<Handler>;
  listeners: Set<StatusListener>;
  consecutiveErrors: number;
  /** Ditolak policy — permanen sampai halaman dimuat ulang. */
  unauthorized: boolean;
  /** Sedang mengambil sesi / membuat channel. */
  connecting: boolean;
  subscribed: boolean;
  retryAttempt: number;
  retryTimer: ReturnType<typeof setTimeout> | null;
};

const registry = new Map<SupabaseClient, Map<string, Entry>>();

function entriesOf(db: SupabaseClient): Map<string, Entry> {
  let map = registry.get(db);
  if (!map) {
    map = new Map();
    registry.set(db, map);
  }
  return map;
}

function notify(entry: Entry, status: LiveStatus): void {
  // Salin dulu: listener boleh melepas dirinya sendiri saat dipanggil.
  for (const listener of [...entry.listeners]) listener(status);
}

function dropChannel(entry: Entry): void {
  const dead = entry.channel;
  entry.channel = null;
  entry.subscribed = false;
  if (dead) void entry.db.removeChannel(dead);
}

function clearRetry(entry: Entry): void {
  if (entry.retryTimer) {
    clearTimeout(entry.retryTimer);
    entry.retryTimer = null;
  }
}

function scheduleRetry(entry: Entry): void {
  if (entry.unauthorized || entry.retryTimer || entry.handlers.size === 0) return;
  const delay =
    RESUBSCRIBE_BACKOFF_MS[
      Math.min(entry.retryAttempt, RESUBSCRIBE_BACKOFF_MS.length - 1)
    ];
  entry.retryAttempt += 1;
  entry.retryTimer = setTimeout(() => {
    entry.retryTimer = null;
    void connect(entry);
  }, delay);
}

function destroyEntry(entry: Entry): void {
  clearRetry(entry);
  dropChannel(entry);
  const map = registry.get(entry.db);
  if (map) {
    map.delete(entry.topic);
    if (map.size === 0) registry.delete(entry.db);
  }
}

async function connect(entry: Entry): Promise<void> {
  if (
    entry.connecting ||
    entry.channel ||
    entry.unauthorized ||
    entry.handlers.size === 0
  ) {
    return;
  }
  entry.connecting = true;

  // JWT harus sudah menempel di soket Realtime SEBELUM subscribe; policy
  // realtime.messages dievaluasi dengan klaim itu. Tanpa sesi, private channel
  // pasti ditolak — langsung turun ke mode degraded, tanpa membuka soket yang
  // percuma. Sesi dibaca ulang tiap kali (bukan disimpan) supaya sambung ulang
  // sesudah backoff membawa token yang segar.
  let token: string | null = null;
  try {
    const {
      data: { session },
    } = await entry.db.auth.getSession();
    token = session?.access_token ?? null;
    if (token) await entry.db.realtime.setAuth(token);
  } catch {
    token = null;
  }
  if (entry.handlers.size === 0) {
    entry.connecting = false;
    return;
  }
  if (!token) {
    entry.connecting = false;
    notify(entry, "DEGRADED");
    return;
  }

  const channel = entry.db.channel(entry.topic, { config: { private: true } });
  entry.channel = channel;

  channel
    .on("broadcast", { event: "*" }, (msg) => {
      if (entry.channel !== channel) return;
      const event = (msg as { event?: string }).event;
      const payload = (msg as { payload?: LivePayload }).payload ?? {};
      if (event === "INSERT" || event === "UPDATE" || event === "DELETE") {
        for (const handler of [...entry.handlers]) handler(event, payload);
      }
    })
    .subscribe((status, err) => {
      // Status dari channel yang sudah dilepas (dan diganti) tidak relevan.
      if (entry.channel !== channel) return;

      if (status === "SUBSCRIBED") {
        entry.consecutiveErrors = 0;
        entry.retryAttempt = 0;
        entry.subscribed = true;
        notify(entry, "SUBSCRIBED");
        return;
      }
      if (
        status !== "CHANNEL_ERROR" &&
        status !== "TIMED_OUT" &&
        status !== "CLOSED"
      ) {
        return;
      }

      entry.subscribed = false;
      notify(entry, "DEGRADED");

      // Ditolak policy → menyambung ulang tak akan pernah berhasil dengan JWT
      // yang sama. Buang channel-nya sekarang, dan jangan pernah coba lagi.
      if (isUnauthorizedChannelError(err)) {
        entry.unauthorized = true;
        dropChannel(entry);
        return;
      }

      if (status === "CLOSED") {
        // phoenix sudah mencabut channel ini dari soket; tak ada rejoin bawaan.
        dropChannel(entry);
        scheduleRetry(entry);
        return;
      }

      entry.consecutiveErrors += 1;
      if (entry.consecutiveErrors >= MAX_CONSECUTIVE_CHANNEL_ERRORS) {
        entry.consecutiveErrors = 0;
        dropChannel(entry);
        scheduleRetry(entry);
      }
    });

  // `.subscribe()` pada channel yang SUDAH bergabung (dibuat pihak lain di luar
  // registry ini) adalah no-op dan callback status di atas tak pernah menyala.
  // Perlakukan sebagai hidup — binding broadcast-nya tetap terpasang.
  const state = (channel as { state?: string }).state;
  if (state === "joined") {
    entry.subscribed = true;
    notify(entry, "SUBSCRIBED");
  }

  entry.connecting = false;
}

let visibilityHooked = false;

/** Saat tab kembali terlihat, channel yang sedang menunggu backoff dicoba lagi
 *  seketika. Dipasang sekali per halaman. */
function ensureVisibilityHook(): void {
  if (visibilityHooked || typeof document === "undefined") return;
  visibilityHooked = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    for (const map of registry.values()) {
      for (const entry of map.values()) {
        if (
          entry.channel ||
          entry.connecting ||
          entry.unauthorized ||
          entry.handlers.size === 0
        ) {
          continue;
        }
        clearRetry(entry);
        void connect(entry);
      }
    }
  });
}

function isDocumentHidden(): boolean {
  return typeof document !== "undefined" && document.hidden === true;
}

/**
 * Berlangganan satu topic broadcast privat.
 *
 * @returns fungsi pembatal — aman dipanggil berkali-kali.
 */
export function subscribeLive(
  db: SupabaseClient,
  topic: string,
  onEvent: (event: LiveEvent, payload: LivePayload) => void,
  opts?: Options
): () => void {
  ensureVisibilityHook();

  const map = entriesOf(db);
  let entry = map.get(topic);
  if (!entry) {
    entry = {
      db,
      topic,
      channel: null,
      handlers: new Set(),
      listeners: new Set(),
      consecutiveErrors: 0,
      unauthorized: false,
      connecting: false,
      subscribed: false,
      retryAttempt: 0,
      retryTimer: null,
    };
    map.set(topic, entry);
  }

  let disposed = false;
  let degradedTimer: ReturnType<typeof setInterval> | null = null;
  // Sempat DEGRADED sejak SUBSCRIBED terakhir? Tick berkala berhenti total saat
  // tab tersembunyi (di bawah), jadi begitu status kembali SUBSCRIBED — baik
  // karena backoff berhasil sendiri maupun dipercepat `visibilitychange` —
  // snapshot terakhir bisa berasal dari sebelum tab disembunyikan. Flag ini
  // memicu SATU refetch penutup saat pulih, lalu direset.
  let hadDegraded = false;

  const stopDegraded = () => {
    if (degradedTimer) {
      clearInterval(degradedTimer);
      degradedTimer = null;
    }
  };

  const startDegraded = () => {
    hadDegraded = true;
    if (disposed || degradedTimer || !opts?.onDegraded) return;
    const every = opts.degradedIntervalMs ?? DEFAULT_DEGRADED_INTERVAL_MS;
    // Sekali segera, lalu berkala: kalau channel gagal saat mount, layar tidak
    // menunggu satu interval penuh sebelum menampilkan apa pun. Tick di tab
    // yang tak terlihat dilewati — tak ada yang membacanya.
    opts.onDegraded();
    degradedTimer = setInterval(() => {
      if (isDocumentHidden()) return;
      opts.onDegraded?.();
    }, every);
  };

  const listener: StatusListener = (status) => {
    if (disposed) return;
    if (status === "SUBSCRIBED") {
      stopDegraded();
      // Tutup jendela yang ditinggalkan tick berkala yang dilewati saat hidden
      // (lihat `isDocumentHidden` di tick) — satu refetch penutup, bukan lagi
      // menunggu event live berikutnya kebetulan datang.
      if (hadDegraded) {
        hadDegraded = false;
        opts?.onDegraded?.();
      }
    } else {
      startDegraded();
    }
  };

  const handler: Handler = (event, payload) => {
    if (!disposed) onEvent(event, payload);
  };

  entry.handlers.add(handler);
  entry.listeners.add(listener);

  if (entry.subscribed) {
    // Channel bersama sudah hidup — pemanggil baru langsung ikut.
  } else if (entry.unauthorized || entry.retryTimer) {
    // Ditolak permanen, atau sedang menunggu backoff: segarkan sendiri dulu.
    startDegraded();
  } else if (!entry.channel && !entry.connecting) {
    void connect(entry);
  }
  // Sisanya: channel sedang bergabung — status pertamanya akan sampai ke
  // listener ini juga.

  return () => {
    if (disposed) return;
    disposed = true;
    stopDegraded();
    entry.handlers.delete(handler);
    entry.listeners.delete(listener);
    if (entry.handlers.size === 0) destroyEntry(entry);
  };
}
